/* ============================================================
   DW Arcade — STACK N WIN (skill game, internal id "foamstack").
   A faithful CLASSIC arcade STACKER, detailing-skinned in DW purple/green:

     • A run of lit LED blocks marches cell-to-cell across a 7-wide matrix.
     • Press STACK to lock the run onto the row below. Blocks that hang past
       the support shear off and fall — losses are PERMANENT (no regrow).
     • The run narrows on a script as you climb (3 → 2 → 1 wide) and the
       march speeds up, so the top prize tiers demand real precision.
     • Lose every block on a row → topple → game over. Reach the top → 15% OFF.

   Height (rows locked) is the score; the cabinet maps it to a reward tier.
   The whole matrix is always on screen — no scroll — like a real cabinet.

   Conforms to the DwArcadeGames module contract (see dw-game-pacman.js), plus:
     press(btn)  — primary action ('a' = STACK)
     controls    — { dpad:false, showLives:false, buttons:[{id,label}] }
   ============================================================ */
(function () {
  'use strict';

  // ---- DW palette (purple chrome + green action, gold = premium win) ----
  var C = {
    bg0: '#0B0618', bg1: '#170A2E', bezel: '#1F1238', bezelEdge: '#3A2563',
    off: 'rgba(58,37,99,0.30)', offEdge: 'rgba(91,63,143,0.40)',
    grid: 'rgba(122,77,219,0.10)',
    litLo: '#138233', litHi: '#27D255', litEdge: '#BFFBCF', litGlow: '#22B84A',
    actLo: '#1FC24A', actHi: '#5BF77F', actEdge: '#ECFFF1', actGlow: '#39FF6E',
    goldLo: '#B8862A', goldHi: '#F2D679', goldEdge: '#FFF3C9', goldGlow: '#D4A437',
    text: '#F7F8FA', dim: '#9A8FBE', pct: '#27D255', urgent: '#E63946'
  };

  var COLS = 7, FREE_ROWS = 17;
  // Scripted narrowing — the signature of a real stacker. Run is 3 wide through
  // the low tiers, 2 across the middle, 1 for the precision top.
  function scheduleW(placed) { return placed < 6 ? 3 : (placed < 12 ? 2 : 1); }

  // Reward ladder drawn on the matrix (row reached → tier). Mirrors the capped
  // backend table for "foamstack". Discounts only — top prize caps at 15% OFF.
  var LINES = [
    { rows: 6, label: '5%' }, { rows: 9, label: '10%' },
    { rows: 12, label: '12%' }, { rows: FREE_ROWS, label: '15%' }
  ];

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  function create(opts) {
    opts = opts || {};
    var onEnd = opts.onEnd || function () {};
    var onScore = opts.onScore || function () {};
    var onState = opts.onState || function () {};

    // Match the screen aspect so the texture fills with no stretch; render tall.
    var aspect = (opts.aspect && opts.aspect > 0) ? opts.aspect : 0.62;
    var H = 940, W = Math.max(300, Math.round(H * aspect));

    var canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    var ctx = canvas.getContext('2d');

    // ---- perf: on touch / weak GPUs the per-frame shadowBlur (run on dozens of
    // cells) and the static-layer redraws (matrix grid + scanlines + vignette =
    // ~550 ops/frame) were the lag source on mobile. We bake the static layers to
    // offscreen canvases (drawn once) and drop the glow blur. LOWFX gates the glow.
    var coarse = false;
    try { coarse = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches); } catch (e) {}
    var LOWFX = coarse || !!(window.DwArcade && window.DwArcade.weakGPU);
    var bgCanvas = null, fgCanvas = null;   // cached: backdrop+bezel+grid  /  scanlines+vignette

    // ---- playfield layout (full matrix, no scroll) ----
    var HUD_H = Math.round(H * 0.062);
    var L = (function () {
      var availH = H - HUD_H - 22;
      var cell = Math.floor(Math.min((W * 0.9) / COLS, availH / FREE_ROWS));
      var mW = cell * COLS, mH = cell * FREE_ROWS;
      var ox = Math.round((W - mW) / 2);
      var bottom = H - 14;            // baseline of row 0
      return { cell: cell, mW: mW, mH: mH, ox: ox, bottom: bottom, top: bottom - mH };
    })();
    // y of the TOP of grid-row r (0 = bottom row)
    function rowY(r) { return L.bottom - (r + 1) * L.cell; }
    function colX(c) { return L.ox + c * L.cell; }

    var stack, active, falling, rows, state, flash, perfectGlow, dropCd, shake, t, stepT, stepMs, title;
    var base = { x: 0, w: COLS };     // solid floor: the first row always lands full
    title = 'STACK N WIN';

    // ---- attract DEMO (self-playing) state ----
    var demo = false;        // when true, an internal AI drives STACK; onEnd is suppressed
    var demoT = 0;           // elapsed time in the current demo run (safety auto-restart)
    var demoRestart = 0;     // >0 = brief win/topple beat counting down before re-init
    var aiCd = 0;            // small reaction delay so drops don't look frame-perfect-robotic

    function setState(s) { if (state !== s) { state = s; onState(s); } }

    function reset() {
      stack = []; falling = []; rows = 0; flash = 0; perfectGlow = 0; dropCd = 0; shake = 0; t = 0; stepT = 0;
      demoT = 0; demoRestart = 0; aiCd = 0;
      spawn(scheduleW(0), 1);
      setState('attract'); onScore(0);
      render();
    }
    function start() { reset(); demo = false; setState('playing'); }
    function stop() { demo = false; setState('attract'); }

    // Self-playing attract loop. Re-inits a run, flags demo, and drives it via the
    // internal AI in update() — onEnd is never called while demo is true; it loops.
    function startDemo() {
      reset();
      demo = true; demoT = 0; demoRestart = 0; aiCd = 0;
      setState('playing');
    }

    function spawn(w, dir) {
      w = clamp(w, 1, COLS);
      stepMs = clamp(0.200 - rows * 0.0092, 0.060, 0.200);  // marches faster as you climb
      active = { x: (dir > 0 ? 0 : COLS - w), w: w, dir: dir, steps: 0 };
      stepT = 0;
    }

    // ---- input ----  (all external input is ignored while the AI is driving)
    function press() { if (demo) return; if (state === 'playing') drop(); }
    function setDir() {} // no joystick — one-button game

    function below() { return stack.length ? stack[stack.length - 1] : base; }

    function drop() {
      if (dropCd > 0) return;
      dropCd = 0.10;
      var b = below();
      var left = Math.max(active.x, b.x), right = Math.min(active.x + active.w, b.x + b.w);
      var w = right - left;
      if (w <= 0) {                                   // nothing landed on support → topple
        for (var c = 0; c < active.w; c++) falling.push(makeFall(active.x + c));
        shake = 1; gameOver(false); return;
      }
      var perfect = (w === active.w) && (active.x === b.x) && (active.w === b.w);
      // shear the overhang into falling debris (permanent loss)
      for (var cx = active.x; cx < left; cx++) falling.push(makeFall(cx));
      for (var cr = right; cr < active.x + active.w; cr++) falling.push(makeFall(cr));
      stack.push({ x: left, w: w, perfect: perfect });
      rows = stack.length; onScore(rows);
      if (perfect) perfectGlow = 1; else if (w < active.w) flash = 0.18;
      if (rows >= FREE_ROWS) { gameOver(true); return; }
      spawn(Math.min(w, scheduleW(rows)), -active.dir);
    }
    function makeFall(col) { return { x: col, lvl: stack.length, vy: 0, fy: 0, a: 1, rot: 0 }; }

    function gameOver(won) {
      setState(won ? 'win' : 'over'); flash = 0.5;
      // In demo, never end the host session — hold a brief win/topple beat (so the
      // top 15% tier is visible) then silently re-init and keep the attract loop going.
      if (demo) { demoRestart = won ? 1.6 : 1.1; return; }
      onEnd({ score: rows, won: won, lives: 0 });
    }

    // ---- demo AI ----
    // Drive the run toward a fully-supported (ideally perfect) landing on below().
    // Returns true if it wants to STACK on this frame.
    function aiWantsDrop() {
      if (!active) return false;
      var b = below();
      var lo = b.x, hi = b.x + b.w - active.w;         // fully-supported x range
      // If we somehow can't fit on the support, drop on max overlap to stay alive.
      if (hi < lo) {
        var ov = Math.min(active.x + active.w, b.x + b.w) - Math.max(active.x, b.x);
        return ov >= active.w - 0;                     // (shouldn't happen: w never grows)
      }
      var perfectX = b.x;                              // exact stack = perfect when widths match
      var wantPerfect = (active.w === b.w);
      if (wantPerfect) {
        // Lock only on the exact column. The march snaps to integer cells, so this is reachable.
        if (active.x === perfectX) return true;
        return false;
      }
      // Narrower than support: any fully-on position is a clean (no-shear) landing.
      // Aim for the centre of the support so the next run still has room to recover.
      var centre = Math.round(b.x + (b.w - active.w) / 2);
      var target = clamp(centre, lo, hi);
      if (active.x === target) return true;
      // Fallback: accept any safe spot at the turnaround so we never stall a full sweep.
      if (active.x >= lo && active.x <= hi && (active.x === lo || active.x === hi)) return true;
      return false;
    }

    function aiTick(dt) {
      // Hold the post-game beat, then quietly re-init and keep looping.
      if (demoRestart > 0) {
        demoRestart -= dt;
        if (demoRestart <= 0) { startDemo(); }
        return;
      }
      demoT += dt;
      if (demoT > 25) { startDemo(); return; }          // safety: never get visually stuck
      if (aiCd > 0) { aiCd -= dt; return; }
      if (state !== 'playing' || dropCd > 0 || !active) return;
      // Let the run visibly march at least one cell before committing, so each
      // placement reads as a deliberate human-timed lock, not an instant snap.
      if (active.steps < 1) return;
      if (aiWantsDrop()) {
        drop();
        aiCd = 0.05;                                    // tiny human-like beat between drops
      }
    }

    // ---- update ----
    function update(dt) {
      dt = Math.min(dt, 0.05); t += dt;
      if (dropCd > 0) dropCd -= dt;
      if (demo) aiTick(dt);
      if (state === 'playing') {
        stepT += dt;
        while (stepT >= stepMs) {                     // discrete LED march
          stepT -= stepMs;
          active.x += active.dir; active.steps++;
          if (active.x <= 0) { active.x = 0; active.dir = 1; }
          else if (active.x >= COLS - active.w) { active.x = COLS - active.w; active.dir = -1; }
        }
      }
      for (var i = falling.length - 1; i >= 0; i--) {
        var f = falling[i]; f.vy += 1500 * dt; f.fy += f.vy * dt; f.a -= dt * 1.3; f.rot += dt * 6;
        if (f.a <= 0) falling.splice(i, 1);
      }
      if (perfectGlow > 0) perfectGlow = Math.max(0, perfectGlow - dt * 1.4);
      if (flash > 0) flash = Math.max(0, flash - dt * 2);
      if (shake > 0) shake = Math.max(0, shake - dt * 3);
      render();
      // Truthy while a real run is live OR the attract demo is looping (the demo
      // keeps ticking even across its brief win/topple re-init beat).
      return state === 'playing' || demo;
    }

    // Bake the static layers once: bgCanvas = backdrop + bezel + unlit grid (drawn
    // first every frame); fgCanvas = scanlines + vignette (drawn last). This removes
    // the matrix-grid loop, the scanline loop and the vignette gradient from the
    // per-frame path — the bulk of the mobile lag.
    function buildStatic() {
      bgCanvas = document.createElement('canvas'); bgCanvas.width = W; bgCanvas.height = H;
      var bgc = bgCanvas.getContext('2d');
      var bg = bgc.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, C.bg1); bg.addColorStop(0.55, C.bg0); bg.addColorStop(1, '#06030F');
      bgc.fillStyle = bg; bgc.fillRect(0, 0, W, H);
      drawBezel(bgc);
      drawMatrixOff(bgc);

      fgCanvas = document.createElement('canvas'); fgCanvas.width = W; fgCanvas.height = H;
      var fgc = fgCanvas.getContext('2d');
      fgc.globalAlpha = 0.05; fgc.fillStyle = '#000';
      for (var y = 0; y < H; y += 3) fgc.fillRect(0, y, W, 1);
      fgc.globalAlpha = 1;
      var v = fgc.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, H * 0.75);
      v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,0.45)');
      fgc.fillStyle = v; fgc.fillRect(0, 0, W, H);
    }

    // ---- render ----
    function render() {
      if (!bgCanvas) buildStatic();
      ctx.save();
      if (shake > 0) ctx.translate((Math.sin(t * 70) * 6) * shake, (Math.cos(t * 90) * 4) * shake);

      ctx.fillStyle = '#06030F'; ctx.fillRect(-20, -20, W + 40, H + 40);   // base fills any shake gap
      ctx.drawImage(bgCanvas, 0, 0);                                       // cached backdrop + bezel + unlit grid
      drawPrizeLines();
      for (var i = 0; i < stack.length; i++) drawRunRow(stack[i].x, stack[i].w, i, stack[i].perfect ? 'gold' : 'lit');
      drawFalling();
      if (state === 'playing') drawRunRow(active.x, active.w, stack.length, 'active');
      drawHud();

      if (perfectGlow > 0) {
        ctx.globalAlpha = perfectGlow; ctx.fillStyle = C.goldHi;
        ctx.font = '800 ' + Math.round(L.cell * 0.5) + 'px Outfit, Arial';
        ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
        if (!LOWFX) { ctx.shadowColor = C.goldGlow; ctx.shadowBlur = 18; }
        ctx.fillText('PERFECT', W / 2, rowY(stack.length - 1) - 8);
        ctx.shadowBlur = 0; ctx.globalAlpha = 1;
      }

      if (state === 'attract') marquee(title, 'CLICK TO INSERT TOKEN');
      else if (state === 'over') banner('TOPPLED!', C.urgent, 'STACKED ' + rows + ' HIGH');
      else if (state === 'win') banner('TOP SHELF!', C.pct, 'DISCOUNT WON! 15% OFF');

      ctx.drawImage(fgCanvas, 0, 0);   // cached scanlines + vignette overlay
      ctx.restore();
    }

    // faint cabinet bezel framing the matrix (drawn to the cached bg context)
    function drawBezel(g2) {
      var pad = Math.round(L.cell * 0.32);
      var x = L.ox - pad, y = L.top - pad, w = L.mW + pad * 2, h = L.mH + pad * 2;
      g2.fillStyle = C.bezel; g2.fillRect(x, y, w, h);
      g2.strokeStyle = C.bezelEdge; g2.lineWidth = 2; g2.strokeRect(x + 1, y + 1, w - 2, h - 2);
      // inner matte
      g2.fillStyle = '#0A0518'; g2.fillRect(L.ox - 3, L.top - 3, L.mW + 6, L.mH + 6);
    }

    // the unlit LED grid — every empty cell as a dim square (drawn to the cached bg context)
    function drawMatrixOff(g2) {
      var g = L.cell, inset = Math.max(2, Math.round(g * 0.07));
      for (var r = 0; r < FREE_ROWS; r++) {
        var y = rowY(r);
        for (var c = 0; c < COLS; c++) {
          var x = colX(c);
          g2.fillStyle = C.off; g2.fillRect(x + inset, y + inset, g - inset * 2, g - inset * 2);
          g2.strokeStyle = C.offEdge; g2.lineWidth = 1;
          g2.strokeRect(x + inset + 0.5, y + inset + 0.5, g - inset * 2 - 1, g - inset * 2 - 1);
        }
      }
    }

    function drawPrizeLines() {
      var rightX = L.ox + L.mW;
      LINES.forEach(function (ln) {
        var y = rowY(ln.rows - 1);                    // line sits at the TOP of the target row
        var hit = rows >= ln.rows;
        var col = C.pct;
        ctx.strokeStyle = col; ctx.globalAlpha = hit ? 0.30 : 0.7;
        ctx.setLineDash([5, 7]); ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(L.ox, y); ctx.lineTo(rightX, y); ctx.stroke();
        ctx.setLineDash([]); ctx.globalAlpha = 1;
        // label tab on the right edge
        ctx.fillStyle = col; ctx.globalAlpha = hit ? 0.55 : 1;
        ctx.font = '700 ' + Math.round(L.cell * 0.3) + 'px DM Sans, Arial';
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText(ln.label + (hit ? ' ✓' : ''), rightX + 6, y);
        ctx.globalAlpha = 1;
      });
    }

    // draw a locked/active run as individually-lit LED cells (with seams)
    function drawRunRow(cellX, w, lvl, kind) {
      var y = rowY(lvl);
      for (var c = 0; c < w; c++) drawCell(colX(cellX + c), y, kind, lvl);
    }

    function drawCell(x, y, kind, lvl) {
      var g = L.cell, inset = Math.max(2, Math.round(g * 0.07));
      var bx = x + inset, by = y + inset, bw = g - inset * 2, bh = g - inset * 2;
      var lo, hi, edge, glow, pulse = 1;
      if (kind === 'active') { lo = C.actLo; hi = C.actHi; edge = C.actEdge; glow = C.actGlow; pulse = 0.55 + 0.45 * Math.abs(Math.sin(t * 7)); }
      else if (kind === 'gold') { lo = C.goldLo; hi = C.goldHi; edge = C.goldEdge; glow = C.goldGlow; }
      else { lo = C.litLo; hi = C.litHi; edge = C.litEdge; glow = C.litGlow; }

      ctx.save();
      // shadowBlur is the per-frame mobile killer (run on every lit cell). On LOWFX
      // drop it for locked cells and keep only a light glow on the marching block.
      ctx.shadowColor = glow;
      ctx.shadowBlur = LOWFX ? (kind === 'active' ? 8 : 0) : (kind === 'active' ? 22 : 12) * (kind === 'active' ? pulse : 1);
      var grd = ctx.createLinearGradient(bx, by, bx, by + bh);
      grd.addColorStop(0, hi); grd.addColorStop(0.5, lo); grd.addColorStop(1, hi);
      ctx.globalAlpha = kind === 'active' ? (0.8 + 0.2 * pulse) : 1;
      ctx.fillStyle = grd; ctx.fillRect(bx, by, bw, bh);
      ctx.shadowBlur = 0;
      // hot top bevel + bottom shade for a chunky LED look
      ctx.fillStyle = edge; ctx.fillRect(bx, by, bw, Math.max(2, Math.round(bh * 0.16)));
      ctx.fillStyle = 'rgba(0,0,0,0.28)'; ctx.fillRect(bx, by + bh - Math.max(2, Math.round(bh * 0.12)), bw, Math.max(2, Math.round(bh * 0.12)));
      ctx.strokeStyle = edge; ctx.globalAlpha = 0.5; ctx.lineWidth = 1;
      ctx.strokeRect(bx + 0.5, by + 0.5, bw - 1, bh - 1);
      ctx.restore();
    }

    function drawFalling() {
      var g = L.cell, inset = Math.max(2, Math.round(g * 0.07));
      falling.forEach(function (f) {
        ctx.save();
        var cx = colX(f.x) + g / 2, cy = rowY(f.lvl) + g / 2 + f.fy;
        ctx.translate(cx, cy); ctx.rotate(f.rot * 0.2);
        ctx.globalAlpha = Math.max(0, f.a);
        if (!LOWFX) { ctx.shadowColor = C.litGlow; ctx.shadowBlur = 10; }
        ctx.fillStyle = C.litLo; ctx.fillRect(-(g - inset * 2) / 2, -(g - inset * 2) / 2, g - inset * 2, g - inset * 2);
        ctx.fillStyle = C.litEdge; ctx.fillRect(-(g - inset * 2) / 2, -(g - inset * 2) / 2, g - inset * 2, 4);
        ctx.restore();
      });
      ctx.globalAlpha = 1;
    }

    function drawHud() {
      var rewardLabel = '—', rewardCol = C.dim;
      for (var i = LINES.length - 1; i >= 0; i--) {
        if (rows >= LINES[i].rows) { rewardLabel = LINES[i].label + ' OFF'; rewardCol = C.pct; break; }
      }
      ctx.fillStyle = 'rgba(8,4,16,0.9)'; ctx.fillRect(0, 0, W, HUD_H);
      ctx.fillStyle = C.bezelEdge; ctx.fillRect(0, HUD_H - 2, W, 2);
      var midY = HUD_H / 2, fs = Math.round(HUD_H * 0.34);
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left'; ctx.fillStyle = C.dim; ctx.font = '700 ' + Math.round(fs * 0.62) + 'px DM Sans, Arial';
      ctx.fillText('HEIGHT', 12, midY);
      ctx.fillStyle = C.litHi; ctx.font = '800 ' + fs + 'px Outfit, Arial';
      ctx.fillText(rows + '/' + FREE_ROWS, 12 + Math.round(fs * 2.6), midY);
      ctx.textAlign = 'right'; ctx.fillStyle = C.dim; ctx.font = '700 ' + Math.round(fs * 0.62) + 'px DM Sans, Arial';
      ctx.fillText('REWARD', W - 12 - Math.round(fs * 4.4), midY);
      ctx.fillStyle = rewardCol; ctx.font = '800 ' + Math.round(fs * 0.92) + 'px Outfit, Arial';
      ctx.fillText(rewardLabel, W - 12, midY);
    }

    // styled arcade marquee for the attract screen
    function marquee(big, sub) {
      var cy = H * 0.44;
      ctx.save();
      ctx.fillStyle = 'rgba(8,4,16,0.74)'; ctx.fillRect(0, cy - H * 0.11, W, H * 0.24);
      ctx.strokeStyle = C.goldGlow; ctx.globalAlpha = 0.5; ctx.lineWidth = 2;
      ctx.strokeRect(8, cy - H * 0.11 + 4, W - 16, H * 0.24 - 8); ctx.globalAlpha = 1;
      // chevron flashers
      var shimmer = 0.5 + 0.5 * Math.sin(t * 4);
      drawChevrons(W * 0.5 - measureBig(big) / 2 - L.cell * 0.9, cy, -1, shimmer);
      drawChevrons(W * 0.5 + measureBig(big) / 2 + L.cell * 0.9, cy, 1, shimmer);
      // logo text — italic, gold, dark outline
      ctx.translate(W / 2, cy); ctx.transform(1, 0, -0.18, 1, 0, 0);
      var fs = bigFontSize(big);
      ctx.font = '800 ' + fs + 'px Outfit, Arial';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      ctx.shadowColor = C.goldGlow; ctx.shadowBlur = 20;
      ctx.strokeStyle = '#3A1F00'; ctx.lineWidth = Math.round(fs * 0.16); ctx.strokeText(big, 0, 0);
      ctx.shadowBlur = 0;
      var grd = ctx.createLinearGradient(0, -fs / 2, 0, fs / 2);
      grd.addColorStop(0, C.goldHi); grd.addColorStop(0.5, C.goldGlow); grd.addColorStop(1, '#A9781F');
      ctx.fillStyle = grd; ctx.fillText(big, 0, 0);
      ctx.restore();
      // subtitle blinks
      ctx.globalAlpha = 0.55 + 0.45 * Math.sin(t * 3);
      ctx.fillStyle = C.text; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = '700 ' + Math.round(L.cell * 0.34) + 'px DM Sans, Arial';
      ctx.fillText(sub, W / 2, cy + H * 0.085); ctx.globalAlpha = 1;
    }
    function bigFontSize(s) { return clamp(Math.round(W / Math.max(7, s.length) * 1.5), 26, Math.round(L.cell * 0.95)); }
    function measureBig(s) { ctx.font = '800 ' + bigFontSize(s) + 'px Outfit, Arial'; return ctx.measureText(s).width; }
    function drawChevrons(x, y, dir, a) {
      var s = L.cell * 0.34;
      ctx.save(); ctx.globalAlpha = 0.4 + 0.6 * a; ctx.fillStyle = C.goldHi; ctx.shadowColor = C.goldGlow; ctx.shadowBlur = 12;
      for (var i = 0; i < 3; i++) {
        var ox = x + dir * i * s * 0.8;
        ctx.beginPath(); ctx.moveTo(ox, y - s); ctx.lineTo(ox + dir * s * 0.7, y); ctx.lineTo(ox, y + s);
        ctx.lineTo(ox + dir * s * 0.3, y + s); ctx.lineTo(ox + dir * s, y); ctx.lineTo(ox + dir * s * 0.3, y - s); ctx.closePath(); ctx.fill();
      }
      ctx.restore();
    }

    function banner(big, color, sub) {
      var cy = H * 0.44;
      ctx.save();
      ctx.fillStyle = 'rgba(8,4,16,0.8)'; ctx.fillRect(0, cy - H * 0.1, W, H * 0.22);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = color; ctx.font = '800 ' + Math.round(L.cell * 0.9) + 'px Outfit, Arial';
      ctx.shadowColor = color; ctx.shadowBlur = 22; ctx.fillText(big, W / 2, cy); ctx.shadowBlur = 0;
      ctx.fillStyle = C.text; ctx.font = '700 ' + Math.round(L.cell * 0.34) + 'px DM Sans, Arial';
      ctx.fillText(sub, W / 2, cy + H * 0.075);
      ctx.restore();
    }

    // (scanlines + vignette are now baked into fgCanvas in buildStatic)

    function drawAttract(name) { if (name) title = String(name).toUpperCase(); reset(); }

    reset();

    return {
      canvas: canvas, width: W, height: H,
      // touch:'tap' — the whole screen is the STACK button (tap anywhere). Desktop
      // keeps Space/click. `buttons` retained only for legacy callers; the 2D engine
      // no longer paints it (gesture layer + keyboard own input now).
      controls: { dpad: false, showLives: false, touch: 'tap', buttons: [{ id: 'a', label: 'STACK' }] },
      // Per-game tutorial shown on the How-to-Play flash (device-aware: m=mobile gesture, d=desktop keys).
      tutorial: [
        { m: { g: 'tap', t: 'Tap anywhere to drop &amp; STACK the row' }, d: { k: ['SPACE'], t: 'Press SPACE to drop &amp; STACK the row' } },
        { m: { g: 'tip', t: 'Time it so blocks land flush — overhang shears off' }, d: { g: 'tip', t: 'Time it so blocks land flush — overhang shears off' } }
      ],
      start: start, stop: stop, reset: reset, setDir: setDir, press: press, update: update,
      demo: startDemo,
      drawAttract: drawAttract,
      getScore: function () { return rows; },
      getLives: function () { return 0; },
      getState: function () { return state; }
    };
  }

  window.DwArcadeGames = window.DwArcadeGames || {};
  window.DwArcadeGames.foamstack = create;
})();
